/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { ReactNode, useMemo } from "react";
import { EditorContext, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import ListItem from "@tiptap/extension-list-item";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import CharacterCount from "@tiptap/extension-character-count";

type TiptapEditorProviderProps = {
  children?: ReactNode;
};

const normalizePastedPreformattedText = (html: string) => {
  const parsedHtml = new DOMParser().parseFromString(html, "text/html");
  const preformattedElements = parsedHtml.querySelectorAll("pre");

  if (preformattedElements.length === 0) {
    return html;
  }

  preformattedElements.forEach((pre) => {
    const paragraph = parsedHtml.createElement("p");
    pre.querySelectorAll("br").forEach((lineBreak) => lineBreak.replaceWith("\n"));
    pre.textContent?.split(/\r\n?|\n/).forEach((line, index) => {
      if (index > 0) {
        paragraph.append(parsedHtml.createElement("br"));
      }
      paragraph.append(line);
    });
    pre.replaceWith(paragraph);
  });

  return parsedHtml.body.innerHTML;
};

export const TiptapEditorProvider = ({ children }: TiptapEditorProviderProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: false,
        code: false,
        codeBlock: false,
        heading: false,
        horizontalRule: false,
        italic: false,
        link: false,
        strike: false,
      }),
      Underline,
      ListItem,
      BulletList,
      OrderedList,
      CharacterCount.configure(),
    ],
    editorProps: {
      transformPastedHTML: normalizePastedPreformattedText,
    },
  });

  const providerValue = useMemo(() => ({ editor }), [editor]);

  return <EditorContext.Provider value={providerValue}>{children}</EditorContext.Provider>;
};
